import { useCallback, useContext } from 'react';

import { OptimizelyContext } from '@optimizely/react-sdk';

import { timeoutFetchSentryGuard } from 'Fetch';
import { authSignUp, getUserSession, postCreateEnquiry } from 'PrivateAPI';

import { ConfigContext } from 'Context/config';
import { UserContext } from 'Context/user';
import { checkMarketPlaceEligibility as checkMarketPlaceEligibilityPartnerId } from 'Context/useSetGaMarketplaceEligible';
import { VehicleLookupContext } from 'Context/vehicleLookup';
import { VehicleContext } from 'Context/vehicles';

import { getSnowplowIds, VALUATIONS_GA_TAGS } from 'Utilities/analytics';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { formatInternationalPhoneNumber, getStrippedUserData } from 'Utilities/helpers';
import { useFeatureFlags } from 'Utilities/hooks';
import { User } from 'Utilities/user/@types';
import { Enquiry, Vehicle } from 'Utilities/vehicles/@types';

import { AuthnSignUpResponse } from 'Services/api.types';

declare global {
  // eslint-disable-next-line vars-on-top, no-var
  var dataLayer: Record<string, any>[];
}

type CreateEnquiryType = {
  consent?: { consent: boolean; marketing: false };
  email?: string;
  mileage: number;
  name?: string;
  phone?: string;
  postcode?: string;
  sale_timeframe?: string; // eslint-disable-line camelcase
  timeframe?: string;
  vrm: string;
};

type CreateEnquiryResponse = {
  code?: string;
  customer?: User | undefined;
  enquiry?: Enquiry;
  error?: string;
  message?: string;
  vehicle?: Vehicle;
};

export const useCreateEnquiryAction = ({ isShouldSignUp } = { isShouldSignUp: false }) => {
  const { configState: { affiliate } } = useContext(ConfigContext);
  const { userActions: { update: updateUser }, userState } = useContext(UserContext);
  const {
    vehicleActions: {
      addWithEnquiry: addVehicleWithEnquiry,
      replace: updateVehicles,
    },
  } = useContext(VehicleContext);
  const { vehicleLookupActions: { add: addVehicleLookup } } = useContext(VehicleLookupContext);
  const { optimizely } = useContext(OptimizelyContext);
  const { useAuthnIdToken } = useFeatureFlags();

  const createEnquiryAction = useCallback(async ({
    consent,
    email,
    mileage,
    name,
    phone,
    postcode,
    sale_timeframe, // eslint-disable-line camelcase
    timeframe,
    vrm,
  }: CreateEnquiryType) => {
    const postCreateEnquiryOptionalParams: Pick<CreateEnquiryType, 'consent' | 'email' | 'name' | 'phone'> = isShouldSignUp ? {} : {
      consent,
      email,
      name,
      phone,
    };
    const postCreateEnquiryHeaders: Record<string, string> = {};
    let cognitoTokens = userState?.cognitoTokens;

    if (isShouldSignUp) {
      const { error, response }: { error?: Error; response: AuthnSignUpResponse | null } = await authSignUp({
        email,
        marketingConsent: !!consent?.consent,
        name,
        phone: formatInternationalPhoneNumber(phone),
        postcode,
      }).then((r) => ({ response: r })).catch((e) => {
        window?.Sentry?.captureException?.(e);
        return ({ error: e, response: null });
      });

      if (error || !response) {
        return {
          error,
        };
      }

      postCreateEnquiryHeaders['x-mway-authorization'] = response.idToken;
      const { message, user, ...tokens } = response;
      cognitoTokens = tokens as User['cognitoTokens'];
    }

    const {
      code,
      customer: enquiryResponseCustomer,
      enquiry,
      error: apiError,
      message,
      vehicle,
    }: CreateEnquiryResponse = await postCreateEnquiry({
      ...postCreateEnquiryOptionalParams,
      affiliate: affiliate?.slug || null,
      app_variant: {},
      mileage,
      postcode,
      sale_timeframe: sale_timeframe || timeframe, // eslint-disable-line camelcase
      source: 'seller-webapp',
      vrm,
      ...getSnowplowIds(),
    }, { include: 'tracker' }, postCreateEnquiryHeaders).catch((error) => {
      timeoutFetchSentryGuard(error, () => window?.Sentry?.captureException(new Error(error), {
        extra: {
          vrm,
        },
        tags: {
          fetch: 'Create enquiry error',
        },
      }));
      return error;
    });

    const customer = { ...enquiryResponseCustomer, cognitoTokens };
    const result = {
      customer,
      enquiry,
      error: apiError || code || message,
      vehicle,
    } as CreateEnquiryResponse;

    if (result.error) { // API Error
      return result as CreateEnquiryResponse;
    }

    if (vehicle) {
      const payload = {
        enquiry,
        vehicle,
      };

      // @ts-ignore
      addVehicleWithEnquiry(payload);
      addVehicleLookup(payload);

      if (enquiry?.id) {
        checkMarketPlaceEligibilityPartnerId({ enquiry, marketplaceEligible: enquiry?.marketplaceEligible });
        VALUATIONS_GA_TAGS.ENQUIRY_CREATED({ customer, enquiry });

        optimizely?.track(EVENT.ENQUIRY_CREATED);
      }
    }

    if (Number.isInteger(customer?.id)) {
      updateUser(customer as Partial<User>);
      updateVehicles(customer?.vehicles as Vehicle[]);

      const userSessionToken = (useAuthnIdToken && customer?.cognitoTokens?.refreshToken) ? {
        refreshToken: customer?.cognitoTokens?.refreshToken,
      } : {
        token: customer?.login_token,
      };

      await getUserSession(userSessionToken)
        .catch((err) => timeoutFetchSentryGuard(err, () => window?.Sentry?.captureException(new Error(err))));
    } else {
      window?.Sentry?.captureException?.(new Error('createEnquiryAction seller ID is not an integer'), {
        extra: {
          customer: getStrippedUserData(customer),
        },
      });
    }

    return result as CreateEnquiryResponse;
  }, [
    addVehicleLookup,
    addVehicleWithEnquiry,
    affiliate?.slug,
    isShouldSignUp,
    optimizely,
    updateUser,
    updateVehicles,
    useAuthnIdToken,
    userState?.cognitoTokens,
  ]);

  return { createEnquiryAction };
};
